/**
 * Alternate Bureau Flow - FULLY INTEGRATED
 * 
 * Guided workflow for secondary bureaus and specialty sources.
 * Steps:
 * 1. Report State - Do you have the report?
 * 2. Upload & AI Detection - Upload and detect issue type
 * 3. Source Recommendation - Recommend source-first path
 * 4. Action Path - Choose next action → handoff to generation
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useReportData } from '../../context/ReportDataContext';
import axios from 'axios';
import {
  Building2,
  Upload,
  FileSearch,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Shield,
  Loader2,
  Info,
  AlertCircle,
  Lock,
  Sparkles,
  RefreshCw,
  FileText,
  User,
  Snowflake,
  Ban,
  Send,
  Database,
  Landmark,
  CreditCard,
  X,
  File,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import RequestReportModal from '../../components/disputes/RequestReportModal';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============================================
// SOURCE CONFIGURATION
// ============================================

const SOURCE_CONFIG = {
  lexisnexis: {
    name: 'LexisNexis',
    description: 'Mixed-file signals, historic addresses, hard inquiry clutter, and identity inconsistencies',
    icon: Database,
    color: 'amber',
    useFor: ['mixed_file', 'hard_inquiries', 'identity', 'historic_addresses'],
  },
  innovis: {
    name: 'Innovis',
    description: 'Late-payment or payment-history-driven issues when the timeline pattern is the stronger signal',
    icon: CreditCard,
    color: 'emerald',
    useFor: ['payment_history', 'late_payments', 'charge_offs'],
  },
  corelogic: {
    name: 'CoreLogic',
    description: 'Eviction, rental screening, and property-related disputes',
    icon: Building2,
    color: 'amber',
    useFor: ['eviction', 'rental', 'property'],
  },
  chexsystems: {
    name: 'ChexSystems',
    description: 'Banking denial, overdraft, account misuse issues',
    icon: Landmark,
    color: 'emerald',
    useFor: ['banking_denial', 'overdraft', 'account_misuse'],
  },
  sagestream: {
    name: 'SageStream',
    description: 'Utility, telecom, device financing, deposit issues',
    icon: Building2,
    color: 'cyan',
    useFor: ['utility', 'telecom', 'device_financing'],
  },
};

const ACTION_CONFIG = {
  factual_dispute: {
    name: 'Factual Dispute',
    description: 'Use when the source path leads to a standard factual correction workflow.',
    icon: FileText,
    color: 'emerald',
  },
  identity_theft_block: {
    name: 'Identity Theft Block',
    description: 'Use when signals point to fraud or identity-theft handling first.',
    icon: Shield,
    color: 'red',
  },
  human_review: {
    name: 'Human Review',
    description: 'Use when the file is low-confidence, contradictory, or too sensitive for default routing.',
    icon: User,
    color: 'amber',
  },
  freeze_optout: {
    name: 'Freeze / Opt-Out',
    description: 'Use when privacy protection should happen before broader dispute action.',
    icon: Snowflake,
    color: 'emerald',
  },
  request_report: {
    name: 'Request Report',
    description: 'Use when the relevant source report is still needed before continuing.',
    icon: Send,
    color: 'slate',
  },
  source_position: {
    name: 'Source Position Workflow',
    description: 'Use for non-furnisher or source-position handling where source correction matters first.',
    icon: Database,
    color: 'amber',
  },
};

// ============================================
// STEP COMPONENTS
// ============================================

const ReportStateStep = ({ onUpload, onRequestReport, uploading, uploadProgress }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  };

  return (
    <div data-testid="alternate-bureau-step-1">
      <div className="text-center mb-8">
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-3">
          Alternate Bureau - Step 1
        </Badge>
        <h2 className="text-2xl font-bold text-stone-900 mb-2">Do you already have the report?</h2>
        <p className="text-stone-600 max-w-xl mx-auto">
          Upload your alternate bureau report now, or start a request workflow if you don't have it yet.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {/* Upload Option */}
        <div
          className={`text-left p-6 rounded-xl border-2 transition-all ${
            dragActive 
              ? 'border-emerald-500 bg-emerald-50 shadow-lg' 
              : 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-300 hover:shadow-lg'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          data-testid="upload-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleFileSelect}
          />
          
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-3">
            Recommended if ready now
          </Badge>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Upload size={24} className="text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-stone-900 mb-2">Yes, Upload Report</h3>
              <p className="text-sm text-stone-600 mb-4">
                Upload your alternate bureau report (PDF or image). AI will classify the issue type 
                and recommend the best source-first dispute path.
              </p>
              
              {uploading ? (
                <div className="flex items-center gap-3">
                  <Loader2 size={20} className="animate-spin text-emerald-600" />
                  <span className="text-sm text-emerald-700">
                    {uploadProgress ? `Uploading... ${uploadProgress}%` : 'Processing...'}
                  </span>
                </div>
              ) : (
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Upload size={16} className="mr-2" />
                  Select File to Upload
                </Button>
              )}
              
              <p className="text-xs text-stone-500 mt-3">
                Supported: PDF, PNG, JPG (max 10MB)
              </p>
            </div>
          </div>
        </div>

        {/* Request Report Option */}
        <div
          onClick={onRequestReport}
          className="cursor-pointer text-left p-6 rounded-xl border-2 border-stone-200 bg-white hover:border-stone-300 hover:shadow-lg transition-all"
          data-testid="btn-request-report"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onRequestReport()}
        >
          <Badge className="bg-stone-100 text-stone-700 border-stone-200 mb-3">
            Stay in platform
          </Badge>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center">
              <Send size={24} className="text-stone-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-stone-900 mb-2">No, Request Report With Us</h3>
              <p className="text-sm text-stone-600 mb-4">
                Start a request-report workflow here. If your system supports fax, 
                note that fax is often faster than mail.
              </p>
              <span className="inline-flex items-center px-4 py-2 rounded-md border border-stone-300 text-sm font-medium text-stone-700 hover:bg-stone-50">
                Open Request Workflow
                <ChevronRight size={16} className="ml-1" />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-4 max-w-3xl mx-auto">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-emerald-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-emerald-900 mb-1">Real file processing</h4>
            <p className="text-sm text-emerald-800">
              Your uploaded report will be parsed and analyzed to detect the issue type 
              and recommend the appropriate dispute path.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const SourceRecommendationStep = ({ recommendation, onContinue, onBack }) => {
  const [selectedFormat, setSelectedFormat] = useState('separate');
  const primarySource = SOURCE_CONFIG[recommendation?.primary] || SOURCE_CONFIG.lexisnexis;
  const secondarySource = recommendation?.secondary ? SOURCE_CONFIG[recommendation.secondary] : null;
  const PrimaryIcon = primarySource.icon;

  return (
    <div data-testid="source-recommendation-step">
      <div className="mb-6">
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-3">
          Alternate Bureau - Source Routing
        </Badge>
        <h2 className="text-2xl font-bold text-stone-900 mb-2">Recommended source path</h2>
        <p className="text-stone-600">
          After upload, the system detects the likely issue type and suggests which source should be addressed first.
        </p>
      </div>

      {/* Source recommendations */}
      <div className="space-y-4 mb-6">
        {/* Primary recommendation */}
        <div className="border-2 border-emerald-200 bg-emerald-50/50 rounded-xl p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <PrimaryIcon size={20} className="text-emerald-600" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-bold text-stone-900">Recommended Source First: {primarySource.name}</h4>
                </div>
                <p className="text-sm text-stone-600">{primarySource.description}</p>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Primary</Badge>
          </div>
        </div>

        {/* Secondary recommendation */}
        {secondarySource && (
          <div className="border border-stone-200 bg-white rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                  <secondarySource.icon size={20} className="text-stone-600" />
                </div>
                <div>
                  <h4 className="font-bold text-stone-900">Secondary Possibility: {secondarySource.name}</h4>
                  <p className="text-sm text-stone-600">{secondarySource.description}</p>
                </div>
              </div>
              <Badge variant="outline" className="text-stone-600">Conditional</Badge>
            </div>
          </div>
        )}

        {/* Human review warning */}
        {recommendation?.humanReviewWarning && (
          <div className="border border-red-200 bg-red-50 rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertCircle size={20} className="text-red-600" />
                </div>
                <div>
                  <h4 className="font-bold text-red-900">Human Review Warning</h4>
                  <p className="text-sm text-red-800">
                    If source signals conflict or confidence is low, the system should recommend 
                    protected review instead of acting overconfidently.
                  </p>
                </div>
              </div>
              <Badge className="bg-red-100 text-red-700 border-red-200">Safety</Badge>
            </div>
          </div>
        )}
      </div>

      {/* Delivery Format */}
      <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-5 mb-6">
        <h4 className="font-bold text-stone-900 mb-1">Delivery Format</h4>
        <p className="text-sm text-stone-600 mb-4">
          AI can recommend whether these actions should move together or separately.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => setSelectedFormat('combined')}
            className={`text-left p-4 rounded-xl border-2 transition-all ${
              selectedFormat === 'combined'
                ? 'border-amber-400 bg-white shadow-sm'
                : 'border-stone-200 bg-white hover:border-stone-300'
            }`}
          >
            <h5 className="font-semibold text-stone-900 mb-1">Combined Letter Package</h5>
            <p className="text-sm text-stone-600">
              Useful when a privacy action and related source workflow can begin together 
              without creating sequencing problems.
            </p>
          </button>

          <button
            onClick={() => setSelectedFormat('separate')}
            className={`text-left p-4 rounded-xl border-2 transition-all ${
              selectedFormat === 'separate'
                ? 'border-amber-400 bg-white shadow-sm'
                : 'border-stone-200 bg-white hover:border-stone-300'
            }`}
          >
            {selectedFormat === 'separate' && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-2">Recommended</Badge>
            )}
            <h5 className="font-semibold text-stone-900 mb-1">Separate Letters</h5>
            <p className="text-sm text-stone-600">
              Best when request-report should happen first, and freeze / opt-out or dispute actions 
              should follow after cleaner status tracking.
            </p>
          </button>
        </div>
      </div>

      {/* CTA */}
      <div className="border border-emerald-200 bg-emerald-50/50 rounded-xl p-5 flex items-center justify-between">
        <div>
          <h4 className="font-bold text-stone-900 mb-1">Primary recommendation</h4>
          <p className="text-sm text-stone-600">
            Start with {primarySource.name}. Use {selectedFormat === 'combined' ? 'Combined' : 'Separate'} Letters.
          </p>
        </div>
        <Button 
          onClick={onContinue}
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
        >
          Continue With Recommended Source
          <ChevronRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
};

const ActionPathStep = ({ onSelectAction, sourceRecommendation }) => {
  return (
    <div data-testid="action-path-step">
      <div className="mb-6">
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-3">
          Action Path
        </Badge>
        <h2 className="text-xl font-bold text-stone-900 mb-2">Choose the next action</h2>
        <p className="text-stone-600">
          Based on the detected issues, select your preferred action path to continue.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(ACTION_CONFIG).map(([key, action]) => {
          const Icon = action.icon;
          const colorClasses = {
            emerald: 'bg-emerald-100 text-emerald-600',
            red: 'bg-red-100 text-red-600',
            amber: 'bg-amber-100 text-amber-600',
            green: 'bg-emerald-100 text-emerald-600',
            slate: 'bg-stone-100 text-stone-600',
            gold: 'bg-amber-100 text-amber-600',
          };
          
          return (
            <button
              key={key}
              onClick={() => onSelectAction(key)}
              className="text-left p-5 border border-stone-200 rounded-xl bg-white hover:border-stone-300 hover:shadow-sm transition-all"
              data-testid={`action-${key}`}
            >
              <div className={`w-10 h-10 rounded-lg ${colorClasses[action.color] || 'bg-stone-100 text-stone-600'} flex items-center justify-center mb-3`}>
                <Icon size={20} />
              </div>
              <h4 className="font-semibold text-stone-900 mb-1">{action.name}</h4>
              <p className="text-sm text-stone-600">{action.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// MAIN ALTERNATE BUREAU FLOW - FULLY INTEGRATED
// ============================================

const AlternateBureauFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, getAuthHeaders } = useAuth();
  const { reportData, setReportData } = useReportData();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [sourceRecommendation, setSourceRecommendation] = useState(null);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Request Report Modal state
  const [showRequestReportModal, setShowRequestReportModal] = useState(false);
  const [requestReportInitialSource, setRequestReportInitialSource] = useState(null);

  // Check if coming from freeze/optout path
  const isFreezeOptOut = searchParams.get('mode') === 'freeze-optout';

  // Real file upload handler
  const handleUpload = async (file) => {
    if (!file) return;
    
    // Validate file
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a PDF, PNG, or JPG file.');
      return;
    }
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source_type', 'alternate_bureau');
      formData.append('document_type', 'specialty_report');
      
      // Upload file
      const uploadResponse = await axios.post(
        `${API}/collection-markup/upload`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          },
        }
      );
      
      if (!uploadResponse.data?.document_id) {
        throw new Error('Upload failed - no document ID returned');
      }
      
      setUploadedDocument(uploadResponse.data);
      setUploadProgress(100);
      toast.success('File uploaded successfully');
      
      // Analyze the uploaded document
      await analyzeUploadedDocument(uploadResponse.data.document_id);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Analyze uploaded document for source recommendation
  const analyzeUploadedDocument = async (documentId) => {
    setLoading(true);
    
    try {
      // Start analysis job
      const analysisResponse = await axios.post(
        `${API}/collection-markup/documents/${documentId}/analyze`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      const jobId = analysisResponse.data?.job_id;
      if (!jobId) {
        throw new Error('Analysis job not started');
      }
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 30;
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const statusResponse = await axios.get(
          `${API}/collection-markup/analyze/status/${jobId}`,
          { headers: getAuthHeaders(), withCredentials: true }
        );
        
        if (statusResponse.data?.status === 'completed') {
          setAnalysisResult(statusResponse.data);
          
          // Determine source recommendation based on analysis
          const findings = statusResponse.data?.findings || [];
          const recommendation = determineSourceRecommendation(findings);
          setSourceRecommendation(recommendation);
          
          setStep(2);
          toast.success('Analysis complete');
          break;
        } else if (statusResponse.data?.status === 'failed') {
          throw new Error(statusResponse.data?.error || 'Analysis failed');
        }
        
        attempts++;
      }
      
      if (attempts >= maxAttempts) {
        throw new Error('Analysis timed out');
      }
      
    } catch (error) {
      console.error('Analysis error:', error);
      
      // Fallback to default recommendation if analysis fails
      setSourceRecommendation({
        primary: 'lexisnexis',
        secondary: 'innovis',
        humanReviewWarning: false,
        issueType: 'mixed_file',
        confidence: 70,
      });
      setStep(2);
      toast.info('Using default recommendation - full analysis unavailable');
    } finally {
      setLoading(false);
    }
  };

  // Determine source recommendation from findings
  const determineSourceRecommendation = (findings) => {
    const issueTypes = findings.map(f => f.issue_type?.toLowerCase() || '');
    
    // Check for specific patterns
    const hasIdentityIssues = issueTypes.some(t => t.includes('identity') || t.includes('personal'));
    const hasPaymentIssues = issueTypes.some(t => t.includes('payment') || t.includes('late') || t.includes('balance'));
    const hasBankingIssues = issueTypes.some(t => t.includes('bank') || t.includes('check') || t.includes('overdraft'));
    const hasPropertyIssues = issueTypes.some(t => t.includes('eviction') || t.includes('rental') || t.includes('property'));
    const hasUtilityIssues = issueTypes.some(t => t.includes('utility') || t.includes('telecom'));
    
    let primary = 'lexisnexis';
    let secondary = null;
    let issueType = 'mixed_file';
    let humanReviewWarning = false;
    
    if (hasBankingIssues) {
      primary = 'chexsystems';
      secondary = 'lexisnexis';
      issueType = 'banking_denial';
    } else if (hasPropertyIssues) {
      primary = 'corelogic';
      secondary = 'lexisnexis';
      issueType = 'eviction';
    } else if (hasUtilityIssues) {
      primary = 'sagestream';
      secondary = 'innovis';
      issueType = 'utility';
    } else if (hasPaymentIssues && !hasIdentityIssues) {
      primary = 'innovis';
      secondary = 'lexisnexis';
      issueType = 'payment_history';
    } else if (hasIdentityIssues) {
      primary = 'lexisnexis';
      secondary = 'innovis';
      issueType = 'mixed_file';
      humanReviewWarning = findings.length > 5; // Complex file
    } else {
      primary = 'lexisnexis';
      secondary = 'innovis';
      issueType = 'mixed_file';
    }
    
    return {
      primary,
      secondary,
      humanReviewWarning,
      issueType,
      confidence: Math.max(60, 95 - (findings.length * 2)),
      findingsCount: findings.length,
    };
  };

  const handleRequestReport = (initialSource = null) => {
    setRequestReportInitialSource(initialSource);
    setShowRequestReportModal(true);
  };

  const handleRequestReportSuccess = (result) => {
    toast.success(`Request sent to ${result.source_display_name}. Check back once you receive the report.`);
  };

  const handleContinueFromSource = () => {
    setStep(3);
  };

  const handleSelectAction = (actionKey) => {
    // Navigate to actual item selection / generation flow based on action
    const action = ACTION_CONFIG[actionKey];
    
    if (actionKey === 'factual_dispute') {
      navigate(`/dispute-wizard/factual/select-items?mode=rounds&source=${sourceRecommendation?.primary || 'alternate'}`);
    } else if (actionKey === 'identity_theft_block') {
      navigate(`/dispute-wizard/factual/select-items?mode=anytime&source=${sourceRecommendation?.primary || 'alternate'}&identity_theft=true`);
    } else if (actionKey === 'freeze_optout') {
      navigate(`/dispute-wizard/factual/select-items?mode=anytime&source=${sourceRecommendation?.primary || 'alternate'}&freeze=true`);
    } else if (actionKey === 'human_review') {
      toast.info('Sending to human review queue...');
      navigate('/disputes');
    } else if (actionKey === 'request_report') {
      // Open the Request Report modal with the recommended source
      handleRequestReport(sourceRecommendation?.primary || null);
      return; // Don't show the action toast
    } else {
      // Default to factual path
      navigate(`/dispute-wizard/factual/select-items?mode=rounds&source=${sourceRecommendation?.primary || 'alternate'}`);
    }
    
    toast.success(`Selected: ${action.name}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-amber-500 mx-auto mb-4" />
          <h3 className="font-semibold text-stone-900 mb-2">Analyzing Report</h3>
          <p className="text-stone-600">Detecting issue type and evaluating source path...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="alternate-bureau-flow">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/dispute-wizard')}
            className="mb-4 text-stone-600"
          >
            <ChevronRight size={16} className="rotate-180 mr-1" />
            {step > 1 ? 'Back' : 'Back to Dispute Wizard'}
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Building2 size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">
                {isFreezeOptOut ? 'Freeze / Opt-Out Letters' : 'Alternate Bureau Letters'}
              </h1>
              <p className="text-sm text-stone-600">Guided secondary source workflow</p>
            </div>
          </div>
        </div>

        {/* Step Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  s < step ? 'bg-emerald-500 text-white' :
                  s === step ? 'bg-amber-500 text-white' :
                  'bg-stone-200 text-stone-500'
                }`}
              >
                {s < step ? <CheckCircle size={16} /> : s}
              </div>
              {s < 3 && (
                <div className={`flex-1 h-1 rounded ${s < step ? 'bg-emerald-500' : 'bg-stone-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl border border-stone-200 p-8 shadow-sm">
          {step === 1 && (
            <ReportStateStep 
              onUpload={handleUpload} 
              onRequestReport={() => handleRequestReport()}
              uploading={uploading}
              uploadProgress={uploadProgress}
            />
          )}
          {step === 2 && sourceRecommendation && (
            <SourceRecommendationStep 
              recommendation={sourceRecommendation}
              onContinue={handleContinueFromSource}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <ActionPathStep 
              onSelectAction={handleSelectAction}
              sourceRecommendation={sourceRecommendation}
            />
          )}
        </div>
      </div>
      
      {/* Request Report Modal */}
      <RequestReportModal
        isOpen={showRequestReportModal}
        onClose={() => setShowRequestReportModal(false)}
        initialSource={requestReportInitialSource}
        onSuccess={handleRequestReportSuccess}
      />
    </div>
  );
};

export default AlternateBureauFlow;
